import React, { useCallback, useEffect, useRef, useState } from "react";
import constants from "../../../../constants";
import Page from "components/Page";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import styles from "../../accountpreferences.module.scss";
import Card from "components/Card";
import StickySidebar from "accountOwner/pages/Scheduler/components/StickySidebar";
import { cacheSideBarActive, handleError } from "utils";
import Text from "components/Text";
import {
  Accordion,
  AccordionItem,
  AccordionItemHeading,
  AccordionItemButton,
  AccordionItemPanel,
} from "react-accessible-accordion";
import { withTranslation } from "react-i18next";
import Table from "components/table";
import {
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  UncontrolledDropdown,
} from "reactstrap";
import moment from "moment";
import ThreeDots from "../../../../assets/images/threedots.svg";
import CustomDropdown from "components/Dropdown";
import {
  deleteDocument,
  getCategoryList,
  updateDocument,
  uploadDocuments,
  useGetProjectDocumentsList,
} from "repositories/ai-assistant-repository";
import { useAccountOwnerCommon } from "lib/Context/AccountOwner";
import {
  useAllActiveOffices,
  useStaffDesignation,
} from "repositories/scheduler-repository";
import useHandleApiError from "hooks/useHandleApiError";
import Empty from "components/Empty";
import { debounce } from "lodash";
import UploadModal from "components/CustomModal/UploadModal";
import useUploadService from "hooks/useUploadService";
import toast from "react-hot-toast";
import DeleteConfirmationModal from "components/DeleteConfirmationModal";
import Loader from "components/Loader";

const PAGE_SIZE = constants.pageSize;
const containerName = "ai-assistant";

function ManageAiDocuments({ t }) {
  const history = useHistory();
  const [pageNumber, setPageNumber] = useState(1);
  const [searchValue, setSearchValue] = useState("");
  const [categoryList, setCategoryList] = useState([]);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);

  const [showLoader, setShowLoader] = useState(false);
  const [confirmDelete, setConfirmDeleteModal] = useState(false);
  const [selectedDocId, setSelectedDocId] = useState("");
  const [isEditMode, setIsEditMode] = useState(false);

  const [isSidebarActive, setSidebarActive] = useState(
    cacheSideBarActive(constants.sidebar.cache.schedulerIsSidebarActive),
  );
  const sortOptions = constants.sortOptions;
  const { upload: uploadImage } = useUploadService();

  const {
    selectedId,
    dataAccessValues,
    profile,
    commonOwnerData: selectedOwner,
    isStaff,
    selectedOwnerIdString,
  } = useAccountOwnerCommon();
  const { data: staffRoleData } = useStaffDesignation();

  const getInitialFilterState = () => {
    const savedFilters = localStorage.getItem("aiDocumentsFilters");
    if (savedFilters) {
      try {
        return JSON.parse(savedFilters);
      } catch (error) {
        console.error("Error parsing saved filters:", error);
      }
    }
    return {
      Offices: [],
      Roles: [],
      Category: [],
      FileType: [],
    };
  };

  const getInitialSortState = () => {
    const savedSort = localStorage.getItem("aiDocumentsSortOption");
    if (savedSort) {
      try {
        return JSON.parse(savedSort);
      } catch (error) {
        console.error("Error parsing saved sort:", error);
      }
    }
    return {
      sortOptionsSelected: "1",
      currentSortBy: 3,
      currentSortDescending: false,
    };
  };

  const [selected, setSelected] = useState(getInitialFilterState);
  const [appliedFilters, setAppliedFilters] = useState(getInitialFilterState);

  const initialSort = getInitialSortState();
  const [currentSortBy, setCurrentSortBy] = useState(initialSort.currentSortBy);
  const [currentSortDescending, setCurrentSortDescending] = useState(
    initialSort.currentSortDescending,
  );
  const { isLoading, isFetching, refetch, data } = useGetProjectDocumentsList(
    pageNumber,
    PAGE_SIZE,
    searchValue,
    selectedId,
    currentSortBy,
    currentSortDescending,
    appliedFilters.Offices,
    appliedFilters.Roles,
    appliedFilters.Category,
    appliedFilters.FileType,
    {
      enabled: !!selectedId,
    },
  );

  useHandleApiError(isLoading, isFetching);

  const rolesData =
    staffRoleData
      ?.map((item) => item?.designations)
      ?.flat()
      ?.map((item) => ({ name: item?.name, id: item?.id })) || [];

  const { data: officeData } = useAllActiveOffices(
    1,
    constants.ACTIVE_OFFICES_PAGE_SIZE,
    selectedId,
  );

  const goToPreviousScreen = () => {
    history.push(constants.routes.accountPreferences);
  };

  const handleSidebarToggle = () => {
    setSidebarActive(!isSidebarActive);
    localStorage.setItem(
      constants.sidebar.cache.schedulerIsSidebarActive,
      !isSidebarActive,
    );
  };

  const handleSearchFilter = (event) => {
    const value = event.target.value;

    if (value.length >= 3 || value.length === 0) {
      const trimmedValue = value ? value.trim() : "";
      setSearchValue(trimmedValue);
      setPageNumber(1);
    }
  };

  const optimisedSearch = useCallback(debounce(handleSearchFilter, 500), []);

  const fetchCategoryList = async () => {
    try {
      const response = await getCategoryList();
      setCategoryList(response);
    } catch (error) {
      handleError(error);
    }
  };

  useEffect(() => {
    fetchCategoryList();
  }, []);

  const handleClose = () => {
    setIsUploadModalOpen(false);
    setIsEditMode(false);
  };

  const uploadFilesToAPI = async (fileObjects) => {
    let uploadedDocuments = [];

    for (let i = 0; i < fileObjects.length; i++) {
      let fileObj = fileObjects[i];

      if (fileObj.file && !fileObj.isUploaded) {
        try {
          const [err, blobData] = await uploadImage(
            fileObj.file,
            containerName,
          );

          if (err) {
            throw new Error(err);
          }

          uploadedDocuments.push({
            documentPath: `${containerName}/${blobData.blobName}`,
            documentName: fileObj.documentName,
          });

          fileObj.documentPath = blobData.blobUrl;
          fileObj.isUploaded = true;
        } catch (error) {
          throw error;
        }
      } else if (fileObj.documentPath && fileObj.isUploaded) {
        uploadedDocuments.push({
          documentPath: fileObj.documentPath,
          documentName: fileObj.documentName,
        });
      }
    }

    return uploadedDocuments;
  };

  const handleSave = async (filesWithData) => {
    try {
      setShowLoader(true);

      const uploadedFilesWithPaths = await uploadFilesToAPI(filesWithData);

      const aiAssistantProjectDocuments = filesWithData.map(
        (fileData, index) => ({
          documentPath:
            uploadedFilesWithPaths[index]?.documentPath ||
            fileData.documentPath,
          DocumentName: fileData.documentName,
          Description: fileData.Description,
          Version: fileData.Version,
          Category: fileData.Category,
          ExpirationDate: fileData.ExpirationDate,
          IsForAllOffice: fileData.IsForAllOffice,
          IsForAllDesignation: fileData.IsForAllDesignation,
          ...(fileData.OfficeIds && { OfficeIds: fileData.OfficeIds }),
          ...(fileData.DesignationIds && {
            DesignationIds: fileData.DesignationIds,
          }),
          ...(isEditMode && selectedDocId && { Id: selectedDocId }),
        }),
      );

      let payload;

      if (isEditMode) {
        const file = aiAssistantProjectDocuments[0];
        payload = {
          OwnerId: selectedId,
          ...file,
        };
        const result = await updateDocument(payload);
        toast.success(result?.message);
      } else {
        payload = {
          OwnerId: selectedId,
          AiAssistantProjectDocuments: aiAssistantProjectDocuments,
        };
        const result = await uploadDocuments(payload);
        toast.success(result?.message);
      }

      refetch();
      setShowLoader(false);
      setIsUploadModalOpen(false);
    } catch (error) {
      setShowLoader(false);
    }
  };

  const CheckBox = ({ title, checked, onChange }) => {
    return (
      <div className="ch-checkbox">
        <label>
          <input
            type="checkbox"
            onChange={(e) => onChange(e.target.checked)}
            checked={checked}
          />
          <span>{title}</span>
        </label>
      </div>
    );
  };

  const handleChange = (section, value, checked) => {
    setSelected((prev) => {
      const updated = checked
        ? [...prev[section], value]
        : prev[section].filter((v) => v !== value);
      return { ...prev, [section]: updated };
    });
  };

  const deleteAIDocument = (documentId) => {
    setSelectedDocId(documentId);
    setConfirmDeleteModal(true);
  };

  const viewDetail = (row) => {
    history.push({
      pathname: constants.routes.aiAssistant.viewManageAiDocuments,
      state: {
        documentId: row?.id,
        isFromSuperAdmin: false,
        categoryList,
        officeData: officeData?.data,
        staffRoleData,
        documentList: data?.items,
        ownerId: selectedId,
      },
      search: selectedOwnerIdString ? `?${selectedOwnerIdString}` : "",
    });
  };

  const confirmDeleteDocument = async () => {
    setShowLoader(true);
    try {
      const resp = await deleteDocument(selectedDocId);
      setConfirmDeleteModal(false);
      setShowLoader(false);
      toast.success(resp?.message);
      refetch();
    } catch (e) {
      setConfirmDeleteModal(false);
      setShowLoader(false);
      toast.error(e.message);
    }
  };

  const handleDownloadDocument = async (documentPath, documentName) => {
    try {
      if (!documentPath) {
        toast.error(t("aiAssistant.documentPathNotAvailable"));
        return;
      }

      setShowLoader(true);
      const response = await fetch(documentPath);
      if (!response.ok) {
        throw new Error(t("aiAssistant.failedToFetchDocument"));
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");

      link.href = url;
      link.download = documentName || documentPath.split("/").pop();
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      setShowLoader(false);
      toast.success(t("aiAssistant.downloadedSuccessfully"));
    } catch (error) {
      setShowLoader(false);
      toast.error(t("aiAssistant.failedToDownloadDocument"));
      handleError(error);
    }
  };

  const renderFilterSection = () => {
    return (
      <Accordion
        preExpanded={["Offices"]}
        className="filter-accordion"
        allowZeroExpanded
        allowMultipleExpanded
      >
        <AccordionItem uuid="a">
          <AccordionItemHeading>
            <AccordionItemButton>{t("offices")}</AccordionItemButton>
          </AccordionItemHeading>
          <AccordionItemPanel>
            <ul className="filter-list">
              {officeData?.data?.map((item) => (
                <CheckBox
                  key={item?.id}
                  title={item?.name}
                  checked={selected?.Offices.includes(item?.id)}
                  onChange={(checked) =>
                    handleChange("Offices", item?.id, checked)
                  }
                />
              ))}
            </ul>
          </AccordionItemPanel>
        </AccordionItem>
        <AccordionItem uuid="b">
          <AccordionItemHeading>
            <AccordionItemButton>{t("role")}</AccordionItemButton>
          </AccordionItemHeading>
          <AccordionItemPanel>
            <ul className="filter-list">
              {rolesData?.map((item) => (
                <CheckBox
                  key={item?.id}
                  title={item?.name}
                  checked={selected?.Roles.includes(item?.id)}
                  onChange={(checked) =>
                    handleChange("Roles", item?.id, checked)
                  }
                />
              ))}
            </ul>
          </AccordionItemPanel>
        </AccordionItem>
        <AccordionItem uuid="c">
          <AccordionItemHeading>
            <AccordionItemButton>
              {t("superAdmin.category")}
            </AccordionItemButton>
          </AccordionItemHeading>
          <AccordionItemPanel>
            <ul className="filter-list">
              {categoryList.map((item) => (
                <CheckBox
                  key={item?.id}
                  title={item?.category}
                  checked={selected?.Category.includes(item?.id)}
                  onChange={(checked) =>
                    handleChange("Category", item?.id, checked)
                  }
                />
              ))}
            </ul>
          </AccordionItemPanel>
        </AccordionItem>
        <AccordionItem uuid="d">
          <AccordionItemHeading>
            <AccordionItemButton>
              {t("aiAssistant.fileType")}
            </AccordionItemButton>
          </AccordionItemHeading>
          <AccordionItemPanel>
            <ul className="filter-list">
              {constants?.fileTypes?.map((item) => (
                <CheckBox
                  key={item?.id}
                  title={item?.title}
                  checked={selected?.FileType?.includes(item?.title)}
                  onChange={(checked) =>
                    handleChange("FileType", item?.title, checked)
                  }
                />
              ))}
            </ul>
          </AccordionItemPanel>
        </AccordionItem>
      </Accordion>
    );
  };

  const renderFilterButtonSection = () => {
    return (
      <div className={["filter-btn-box"]}>
        <button
          className={"button button-round button-shadow mr-3"}
          title="apply"
          onClick={handleApply}
        >
          {t("apply")}
        </button>
        <button
          className={"button button-round button-border button-dark reset-btn"}
          title="reset"
          onClick={handleReset}
        >
          {t("reset")}
        </button>
      </div>
    );
  };

  const handleApply = () => {
    setAppliedFilters(selected);
    localStorage.setItem("aiDocumentsFilters", JSON.stringify(selected));
    setPageNumber(1);
  };

  const handleReset = () => {
    const resetState = {
      Offices: [],
      Roles: [],
      Category: [],
      FileType: [],
    };
    setSelected(resetState);
    setAppliedFilters(resetState);
    setSearchValue("");
    setPageNumber(1);

    localStorage.removeItem("aiDocumentsFilters");
  };
  const preservedRef = useRef(null);
  useEffect(() => {
    if (selectedId) {
      if (preservedRef.current === null) {
        preservedRef.current = selectedId;
      } else if (preservedRef.current !== selectedId) {
        preservedRef.current = selectedId;
        handleReset();
      }
    }
  }, [selectedId]);
  // Check if any filters are applied
  const hasActiveFilters = () => {
    return (
      appliedFilters.Offices.length > 0 ||
      appliedFilters.Roles.length > 0 ||
      appliedFilters.Category.length > 0 ||
      appliedFilters.FileType.length > 0
    );
  };

  const columns = [
    {
      attrs: { datatitle: t("aiAssistant.documentName") },
      dataField: "documentName",
      text: t("aiAssistant.documentName"),
      formatter: (cellContent, row) => {
        if (!isLoading)
          return (
            <span onClick={() => viewDetail(row)}>
              {cellContent ? cellContent : "--"}
            </span>
          );
        return <div className="text-placeholder-150 shimmer-animation"></div>;
      },
    },
    {
      attrs: { datatitle: t("superAdminCategories.categoryName") },
      dataField: "categoryName",
      text: t("superAdminCategories.categoryName"),
      formatter: (cellContent) => {
        if (!isLoading) return <span>{cellContent ? cellContent : "--"}</span>;
        return <div className="text-placeholder-150 shimmer-animation"></div>;
      },
    },
    {
      attrs: { datatitle: t("aiAssistant.version") },
      dataField: "version",
      text: t("aiAssistant.version"),
      formatter: (cellContent) => {
        if (!isLoading) return <span>{cellContent ? cellContent : "--"}</span>;
        return <div className="text-placeholder-150 shimmer-animation"></div>;
      },
    },
    {
      attrs: { datatitle: t("aiAssistant.fileType") },
      dataField: "documentType",
      text: t("aiAssistant.fileType"),
      formatter: (cellContent) => {
        if (!isLoading) return <span>{cellContent ? cellContent : "--"}</span>;
        return <div className="text-placeholder-150 shimmer-animation"></div>;
      },
    },
    {
      attrs: { datatitle: t("aiAssistant.uploadedOn") },
      dataField: "createdAt",
      text: t("aiAssistant.uploadedOn"),
      formatter: (cellContent) => {
        if (!isLoading)
          return (
            <span>
              {cellContent ? moment(cellContent).format("MMM Do, YYYY") : "--"}
            </span>
          );
        return <div className="text-placeholder-150 shimmer-animation"></div>;
      },
    },
    {
      attrs: { datatitle: t("aiAssistant.expirationDate") },
      dataField: "expirationDate",
      text: t("aiAssistant.expirationDate"),
      formatter: (cellContent) => {
        if (!isLoading)
          return (
            <span>
              {cellContent ? moment(cellContent).format("MMM Do, YYYY") : "--"}
            </span>
          );
        return <div className="text-placeholder-150 shimmer-animation"></div>;
      },
    },
    {
      attrs: { datatitle: t("waitingList.action") },
      dataField: "action",
      text: t("waitingList.action"),
      formatter: (cellContent, row) => (
        <UncontrolledDropdown>
          <DropdownToggle caret={false} tag="div" className="cursor-pointer">
            <img src={ThreeDots} alt="ThreeDots" />
          </DropdownToggle>
          <DropdownMenu end className={styles["office-dropdown-menu"]}>
            <DropdownItem className={styles["office-dropdown-item"]}>
              <div onClick={() => viewDetail(row)}>
                {" "}
                {t("waitingList.view")}
              </div>
            </DropdownItem>
            {/* Intentionally commented out */}
            {/* <DropdownItem className={styles["office-dropdown-item"]}>
              <div
                onClick={() => {
                  setSelectedDocId(row?.id);
                  setIsEditMode(true);
                  setIsUploadModalOpen(true);
                }}
              >
                {t("aiAssistant.edit")}
              </div>
            </DropdownItem> */}
            <DropdownItem className={styles["office-dropdown-item"]}>
              <div
                onClick={() =>
                  handleDownloadDocument(row?.documentPath, row?.documentName)
                }
              >
                {t("aiAssistant.download")}
              </div>
            </DropdownItem>
            <DropdownItem className={styles["office-dropdown-item"]}>
              <div onClick={() => deleteAIDocument(row?.id)}>
                {t("aiAssistant.delete")}
              </div>
            </DropdownItem>
          </DropdownMenu>
        </UncontrolledDropdown>
      ),
    },
  ];

  const [sortOptionsSelected, setsortOptionsSelected] = useState(
    initialSort.sortOptionsSelected,
  );

  const handleViewChange = (value) => {
    setsortOptionsSelected(value);

    const selectedOption = sortOptions.find((option) => option.id === value);
    if (selectedOption) {
      setCurrentSortBy(selectedOption.sortBy);
      setCurrentSortDescending(selectedOption.sortDescending);

      // Save sort option to localStorage
      const sortState = {
        sortOptionsSelected: value,
        currentSortBy: selectedOption.sortBy,
        currentSortDescending: selectedOption.sortDescending,
      };
      localStorage.setItem("aiDocumentsSortOption", JSON.stringify(sortState));
    }
  };

  let rows = [];
  let totalItems = 0;
  if (!isLoading && data && data?.items) {
    rows = data.items;
    totalItems = data?.totalCount;
  } else {
    rows = new Array(PAGE_SIZE).fill({});
    totalItems = PAGE_SIZE;
  }

  // Check if user has admin access
  if (
    !dataAccessValues?.isAdmin &&
    profile?.role?.systemRole == constants.systemRoles.staff
  ) {
    return (
      <Page onBack={goToPreviousScreen} className="scheduler-page">
        <div className={styles.manage_ai_title_wrapper}>
          <div>
            <h2 className="title m-0">{t("aiAssistant.manageAiDocuments")}</h2>
            <p className="subtitle m-0">
              {t("aiAssistant.aiAssistSubheading")}
            </p>
          </div>
        </div>
        {isStaff && selectedOwner?.id && (
          <div className="show-owner-name-text marginBottom30 mt-3">
            {t("aiAssistant.showingAiManagement")}{" "}
            <span>
              '{selectedOwner.firstName} {selectedOwner.lastName}'
            </span>
          </div>
        )}
        <Card
          className={`tabs-right-col appointment-tab-right-col overflow-auto`}
        >
          <Empty Message={t("You do not have access to manage the document")} />
        </Card>
      </Page>
    );
  }

  return (
    <Page onBack={goToPreviousScreen} className="scheduler-page">
      {showLoader && <Loader />}
      <div className={styles.manage_ai_title_wrapper}>
        <div>
          <h2 className="title m-0">{t("aiAssistant.manageAiDocuments")}</h2>
          <p className="subtitle m-0">{t("aiAssistant.aiAssistSubheading")}</p>
        </div>
        <button
          onClick={() => setIsUploadModalOpen(true)}
          className="button button-round button-shadow"
        >
          {t("aiAssistant.uploadDocument")}
        </button>
      </div>
      {isStaff && selectedOwner?.id && (
        <div className="show-owner-name-text marginBottom30 mt-3">
          {t("aiAssistant.showingAiManagement")}{" "}
          <span>
            '{selectedOwner.firstName} {selectedOwner.lastName}'
          </span>
        </div>
      )}
      <Card
        className={`tabs-right-col appointment-tab-right-col overflow-auto`}
      >
        <div className={styles.sidebar_container}>
          <StickySidebar
            isSidebarActive={isSidebarActive}
            handleSidebarToggle={handleSidebarToggle}
            resetFilter={handleReset}
          >
            <div style={{ position: "relative", display: "inline-block" }}>
              <Text size="16px" weight="600" color="#102C42">
                {t("aiAssistant.filters")}
              </Text>
              {hasActiveFilters() && (
                <span className={styles.indicator_badge}></span>
              )}
            </div>
            {renderFilterSection()}
            {renderFilterButtonSection()}
          </StickySidebar>

          <div className={styles.manage_ai_right_sidebar}>
            <div className={styles.dropdown_searchbar_container}>
              <CustomDropdown
                options={sortOptions}
                selectedOption={sortOptionsSelected}
                selectOption={handleViewChange}
              />
              <div className={styles.vertical_divider}></div>
              <div className="search-box">
                <input
                  name="searchTerm"
                  type="text"
                  placeholder={t("aiAssistant.search")}
                  onInput={optimisedSearch}
                />
                <span className="ico">
                  <img
                    src={require("assets/images/search-icon.svg").default}
                    alt="icon"
                  />
                </span>
              </div>
            </div>
            <div className="test-document-table-box w-100">
              {rows && rows.length > 0 && (
                <Table
                  keyField="id"
                  data={rows}
                  columns={columns}
                  bordered={false}
                  className="change-heading"
                  handlePagination={setPageNumber}
                  pageNumber={pageNumber}
                  totalItems={totalItems}
                  pageSize={PAGE_SIZE}
                />
              )}
              {!isLoading && totalItems === 0 && (
                <Empty
                  Message={t("aiAssistant.noRecord", {
                    item: t("aiAssistant.document"),
                  })}
                />
              )}
            </div>
          </div>
        </div>
      </Card>

      {isUploadModalOpen && (
        <UploadModal
          isOpen={isUploadModalOpen}
          setIsOpen={() => setIsUploadModalOpen(!isUploadModalOpen)}
          onClose={handleClose}
          onSave={handleSave}
          existingDocuments={data?.items || []}
          staffRoleData={staffRoleData}
          officeData={officeData?.data}
          categoryList={categoryList}
          isEditMode={isEditMode}
          fromSuperAdmin={false}
        />
      )}
      {confirmDelete && (
        <DeleteConfirmationModal
          closeDeleteModal={() => setConfirmDeleteModal(false)}
          confirmDelete={confirmDelete}
          confirmDeleteMessage={confirmDeleteDocument}
          deleteMessage={t("aiAssistant.deleteMessage")}
          title={t("aiAssistant.deleteModalTitle")}
          btnText={t("delete")}
        />
      )}
    </Page>
  );
}

export default withTranslation()(ManageAiDocuments);
